import { MAX_INSTRUCTIONS_PER_REWRITE } from '@lyrmo/shared';
import { useEffect, useMemo } from 'react';
import { apiClient, type RewriteClient } from '../../api/client';
import { useOptionalAccount } from '../account/account-context';
import { RewriteEditor } from './RewriteEditor';
import type { AccountDrafts } from './useSavedDrafts';
import { useStoredOptions } from './useStoredOptions';

// Where earlier versions kept the draft in this browser. It isn't kept any more, so what's left
// there is deleted.
const OLD_DRAFT_KEYS = ['lyrmo.draft', 'lyrmo.save-draft'];

/**
 * The site's editor, with the options kept in this browser; the draft isn't kept, unless the
 * writer saves it, to their account or as a file. Inside an AccountProvider it also keeps the
 * credit allowance up to date and offers plans at the limit.
 */
export function RewriteWorkspace({
  client = apiClient,
  active = true,
}: {
  client?: RewriteClient;
  active?: boolean;
}) {
  const account = useOptionalAccount();
  const [options, setOptions] = useStoredOptions();
  const current = account?.account;
  // Undefined until the account loads; null for a visitor, who keeps no drafts there.
  const owner = current ? (current.user?.email ?? null) : undefined;
  const accountClient = account?.client;
  const openDialog = account?.openDialog;
  const accountDrafts = useMemo<AccountDrafts | undefined>(
    () =>
      accountClient &&
      openDialog && {
        owner,
        client: accountClient,
        onSignIn: () => {
          openDialog('sign-in');
        },
      },
    [owner, accountClient, openDialog],
  );

  useEffect(() => {
    try {
      for (const key of OLD_DRAFT_KEYS) localStorage.removeItem(key);
    } catch {
      // Storage can be unavailable, as in private windows.
    }
  }, []);

  return (
    <RewriteEditor
      client={client}
      active={active}
      dictationAccount={current?.user?.email}
      options={options}
      onOptionsChange={setOptions}
      creditsLeft={account?.account?.usage.remaining}
      onUsage={account?.applyUsage}
      onSeePlans={
        // Max is the biggest plan, so there's nothing more to offer there.
        account && account.account?.plan !== 'max'
          ? () => {
              account.openDialog('plans', 'limit');
            }
          : undefined
      }
      customTones={account?.customTones}
      onManageTones={
        account
          ? () => {
              account.openDialog('tones');
            }
          : undefined
      }
      onNewTone={account?.makeTone}
      savedInstructions={account?.savedInstructions}
      onManageInstructions={
        account
          ? () => {
              account.openDialog('instructions');
            }
          : undefined
      }
      onNewInstruction={
        account
          ? (text) => {
              account.saveInstruction({
                text,
                // Saved, the rewrites follow it: in place of the instruction it was typed as.
                onSaved: ({ id }) => {
                  const chosen = options.savedInstructions ?? [];
                  if (chosen.length >= MAX_INSTRUCTIONS_PER_REWRITE) return;
                  setOptions({
                    ...options,
                    ...(text && { instruction: undefined }),
                    savedInstructions: [...chosen, id],
                  });
                },
              });
            }
          : undefined
      }
      aiTools={account?.aiTools}
      menuSettings={account?.menuSettings}
      onManageAiTools={
        account
          ? () => {
              account.openDialog('ai-tools');
            }
          : undefined
      }
      onNewAiTool={account?.makeAiTool}
      accountDrafts={accountDrafts}
    />
  );
}
